import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { isValidContactInfoFilter } from '@/features/leads/workflow/queue'
import { trackEvent } from '@/shared/api/telemetry'
import type { FilterValue, SortDir } from './ColumnFilter'
import { STAGES } from '@/shared/domain'
import { stageLabel } from '@/shared/pipelineColors'
import { regionOfLocation, cityOfLocation, SE_REGION_ORDER } from '@/shared/regions'
import type { LeadRow } from '@/shared/types'
import {
  DEFAULT_SORT,
  EMAIL_STATUS_LABEL,
  EMPTY_FILTERS,
  LEAD_DATE_ORDER,
  LS_KEY,
  LS_KEY_LEGACY,
  MAX_SORTS,
  SORT_LABEL,
  MEETING_DATE_ORDER,
  MOVE_DATE_ORDER,
  NO_SCORE_LABEL,
  SCORE_BUCKETS,
  STATE_VERSION,
  UNCLAIMED_LABEL,
  applySort,
  buildValues,
  compareBySorts,
  countActiveFilters,
  evaluateFilters,
  firstPageSortParam,
  isValidDoNotContactFilter,
  leadDateBucketOf,
  meetingDateBucketOf,
  moveDateBucketOf,
  nextHeaderSort,
  ownerLabelOf,
  passesAll,
  scoreBucketOf,
  sfStatusLabelOf,
  passesExcept,
  prepareFilters,
  sortDirOf,
  sortRankOf,
  type ColumnFilterKey,
  type ColumnSort,
  type Filters,
  type SortKey,
} from './columns'
import type { SetFilter } from './filterModel'
import { useBrand } from '@/shared/white-label/useBrand'
import { readStored, writeStored } from '@/shared/lib/storage'

/** v1 stored one sort and bare values: `company: "acme"`, `region: [...]`.
 *  v2 stores a chain and operator-carrying shapes. */
const V1 = 1
// v2 held a score filter and could hold a score sort. CRM-84 removed the
// column, so v2 is readable as v3 once those are dropped — which matters,
// because discarding it instead would reset every BDR's configured table.
const V2 = 2
// v3 gained no filter that v4 lacks — v4 only ADDS the Salesforce-status
// column's filter, and decodeFilters starts from EMPTY_FILTERS, so a v3 blob
// simply arrives with that one filter inactive. Read it forward rather than
// discarding it: a version bump that resets every BDR's configured table is a
// worse outcome than the column starting unfiltered, which is what they want
// anyway.
const V3 = 3
// v4 lacked the score column YCRM-12 brought back. A v4 blob arrives with
// scoreBucket/scoreRange inactive, the same forward-compatible shape as v3
// gaining sfStatus — and, unlike the v2 -> v3 bump, a v2 blob's score filter
// and sort are no longer stripped on the way through: the column they named
// exists again, so decodeFilters and the SORT_LABEL check both accept them.
const V4 = 4

export function encodeFilters(filters: Filters): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).map(([k, v]) => {
      if (v && typeof v === 'object' && 'values' in v) {
        return [k, { mode: (v as SetFilter).mode, values: Array.from((v as SetFilter).values) }]
      }
      return [k, v]
    }),
  )
}

export function serializeState(filters: Filters, sorts: ColumnSort[]): string {
  return JSON.stringify({ version: STATE_VERSION, sorts, filters: encodeFilters(filters) })
}

/** Bring a v1 blob forward rather than discarding it.
 *
 * Everyone with a saved table view has one of these, and dropping them on the
 * version bump would silently reset the filters we went to the trouble of
 * preserving through the storage-key rename. Text becomes a `contains`
 * operator, a value array becomes an include-set, the single sort becomes a
 * one-element chain, and the new ranges start inactive.
 */
export function migrateV1(parsed: Record<string, unknown>): { filters: unknown; sorts: unknown } {
  const src = (parsed.filters || {}) as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(src)) {
    if (Array.isArray(v)) out[k] = { mode: 'include', values: v }
    else if (typeof v === 'string' && k !== 'global' && k !== 'contactInfo') {
      out[k] = { op: 'contains', value: v }
    } else out[k] = v
  }
  return { filters: out, sorts: parsed.sort ? [parsed.sort] : undefined }
}

export function decodeFilters(raw: Record<string, unknown>): Filters {
  const out = { ...EMPTY_FILTERS } as unknown as Record<string, unknown>
  const empties = EMPTY_FILTERS as unknown as Record<string, unknown>
  for (const [k, v] of Object.entries(raw)) {
    const empty = empties[k]
    if (empty === undefined) continue          // a key we no longer have
    if (empty instanceof Object && 'values' in (empty as object)) {
      const o = v as { mode?: string; values?: unknown }
      if (!Array.isArray(o?.values)) continue
      out[k] = {
        mode: o.mode === 'exclude' ? 'exclude' : 'include',
        values: new Set((o.values as unknown[]).filter(x => typeof x === 'string') as string[]),
      }
    } else if (empty instanceof Object && 'op' in (empty as object)) {
      // Shape-check rather than trust: a hand-edited or stale entry must not
      // be able to put the table into a filter mode the UI cannot clear.
      if (!v || typeof v !== 'object' || typeof (v as { op?: unknown }).op !== 'string') continue
      out[k] = { ...(empty as object), ...(v as object) }
    } else if (k === 'contactInfo') {
      if (isValidContactInfoFilter(v)) out[k] = v
    } else if (k === 'doNotContact') {
      if (isValidDoNotContactFilter(v)) out[k] = v
    } else if (k === 'global' && typeof v === 'string') {
      out[k] = v
    }
  }
  return out as unknown as Filters
}

export function loadState(): { filters: Filters; sorts: ColumnSort[] } | null {
  try {
    const raw = readStored(LS_KEY, { legacyKey: LS_KEY_LEGACY })
    if (!raw) return null
    let parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (parsed.version === V1) {
      const m = migrateV1(parsed)
      parsed = { version: STATE_VERSION, filters: m.filters, sorts: m.sorts }
    }
    if (parsed.version === V2 || parsed.version === V3 || parsed.version === V4) {
      parsed = { ...parsed, version: STATE_VERSION }
    }
    if (parsed.version !== STATE_VERSION) return null
    // The key has to be one the comparator still handles. A stored sort on a
    // removed column would otherwise reach `compareBy`, match no case, and
    // return undefined — the table would order by NaN and look broken.
    const sorts: ColumnSort[] = Array.isArray(parsed.sorts)
      ? (parsed.sorts as ColumnSort[]).filter(
          s => s && typeof s.key === 'string' && s.key in SORT_LABEL
            && (s.dir === 'asc' || s.dir === 'desc'),
        ).slice(0, MAX_SORTS)
      : [DEFAULT_SORT]
    return {
      filters: decodeFilters((parsed.filters || {}) as Record<string, unknown>),
      sorts: sorts.length ? sorts : [DEFAULT_SORT],
    }
  } catch {
    return null
  }
}

/**
 * The sort to fetch Tabell's first page in, from the table state the seller
 * left behind. Null when a filter is on or the sort is one the server does not
 * reproduce: those first rows could be none of the rows Tabell would show.
 */
export function storedFirstPageSort(): string | null {
  const state = loadState()
  if (state && countActiveFilters(state.filters) > 0) return null
  return firstPageSortParam(state?.sorts ?? [DEFAULT_SORT])
}

export interface TableValues {
  region: FilterValue[]
  city: FilterValue[]
  score: FilterValue[]
  phone: FilterValue[]
  email: FilterValue[]
  callCount: FilterValue[]
  gotContact: FilterValue[]
  rating: FilterValue[]
  stage: FilterValue[]
  sfStatus: FilterValue[]
  booked: FilterValue[]
  likely: FilterValue[]
  leadDate: FilterValue[]
  moveDate: FilterValue[]
  meetingDate: FilterValue[]
  seller: FilterValue[]
  owner: FilterValue[]
}

export interface TableState {
  sorts: ColumnSort[]
  filters: Filters
  setFilter: <K extends keyof Filters>(key: K, value: Filters[K]) => void
  setFilters: Dispatch<SetStateAction<Filters>>
  sorted: LeadRow[]
  sortByColumn: (k: SortKey, numeric: boolean) => void
  sortFor: (k: SortKey) => SortDir
  sortRank: (k: SortKey) => number
  onSortChange: (k: SortKey) => (dir: SortDir) => void
  clearSorts: () => void
  clearFilters: () => void
  activeFilterCount: number
  values: TableValues
}

export function useTableState(leads: LeadRow[]): TableState {
  const { person, anonymised, roster } = useBrand()
  const initial = useMemo(() => loadState(), [])
  const [sorts, setSorts] = useState<ColumnSort[]>(initial?.sorts || [DEFAULT_SORT])
  const [filters, setFilters] = useState<Filters>(initial?.filters || EMPTY_FILTERS)

  // Persist sort + filters across reloads so Ebba doesn't redo her view.
  useEffect(() => {
    writeStored(LS_KEY, serializeState(filters, sorts))
  }, [filters, sorts])

  // How many rows the current filter set leaves visible. Held in a ref rather
  // than read as a value, so a background refetch changing the count cannot
  // re-arm the search timer below and report the same query twice.
  const resultsRef = useRef(0)

  // Track search usage once a query settles — length and result count, never
  // the text. The debounce is what keeps this one event per search instead of
  // one per keystroke; a fruitless search gets a second, countable event so
  // "searched and found nothing" is a number rather than a metadata filter.
  useEffect(() => {
    const q = filters.global.trim()
    if (!q) return
    const t = setTimeout(() => {
      const results = resultsRef.current
      trackEvent('search_run', { len: q.length, results })
      if (results === 0) trackEvent('search_no_results', { len: q.length })
    }, 500)
    return () => clearTimeout(t)
  }, [filters.global])

  // Stable, so a memoized child (the search box) can depend on it without
  // being rebuilt on every render of the table.
  const setFilter = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
    if (key !== 'global') {
      const applied =
        value instanceof Set ? value.size > 0
        : typeof value === 'string'
          ? (key === 'contactInfo' || key === 'doNotContact' ? value !== 'all' : value.trim().length > 0)
        : false
      if (applied) trackEvent('filter_applied', { column: key })
    }
    setFilters(f => ({ ...f, [key]: value }))
  }, [])

  // Every column filter, evaluated for every lead, once. Both the visible row
  // set and all 14 dropdowns read off this — previously each of them ran its
  // own full pass, so a single keystroke re-filtered the list 15 times.
  const evals = useMemo(() => {
    const prepared = prepareFilters(filters)
    return leads.map(l => evaluateFilters(l, filters, prepared))
  }, [leads, filters])

  // Sort/filter pipeline. Filter first (cheaper to sort the smaller set).
  const filtered = useMemo(
    () => leads.filter((_, i) => passesAll(evals[i])),
    [leads, evals],
  )

  useEffect(() => {
    resultsRef.current = filtered.length
  }, [filtered])

  const sorted = useMemo(() => {
    const out = [...filtered]
    out.sort((a, b) => compareBySorts(a, b, sorts))
    return out
  }, [filtered, sorts])

  // For each column: the rows that *would* be visible given all OTHER filters
  // (Excel does this — the dropdown shows values from the rows not yet
  // excluded by this column's own filter).
  const rowsWithoutFilter = useCallback(
    (skip: ColumnFilterKey): LeadRow[] => leads.filter((_, i) => passesExcept(evals[i], skip)),
    [leads, evals],
  )

  // Sorting a column puts it at the front of the chain and keeps the previous
  // columns behind it as tie-breakers, so "stage, then city" is two ordinary
  // clicks. Asking for the direction a column already has removes it.
  const applyColumnSort = useCallback((k: SortKey, dir: SortDir) => {
    trackEvent('sort_changed', { column: k, dir })
    setSorts(prev => applySort(prev, k, dir))
  }, [])

  const sortByColumn = (k: SortKey, numeric: boolean) => {
    applyColumnSort(k, nextHeaderSort(sortDirOf(sorts, k), numeric))
  }

  const sortFor = (k: SortKey): SortDir => sortDirOf(sorts, k)
  const sortRank = (k: SortKey): number => sortRankOf(sorts, k)
  const onSortChange = (k: SortKey) => (dir: SortDir) => applyColumnSort(k, dir)
  const clearSorts = () => setSorts([DEFAULT_SORT])

  const clearFilters = () => setFilters(EMPTY_FILTERS)

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters])

  // === Filter values (computed against other-filters'd row set) =========
  const regionValues = useMemo(
    () => buildValues(rowsWithoutFilter('region'), l => regionOfLocation(l.location), SE_REGION_ORDER),
    [rowsWithoutFilter])
  const cityValues = useMemo(
    () => buildValues(rowsWithoutFilter('city'), l => cityOfLocation(l.location)),
    [rowsWithoutFilter])
  const scoreValues = useMemo(() => {
    const rows = rowsWithoutFilter('scoreBucket')
    return buildValues(rows, l => scoreBucketOf(l.score), [...SCORE_BUCKETS.map(b => b.label), NO_SCORE_LABEL])
  }, [rowsWithoutFilter])
  const phoneValues = useMemo(
    () => buildValues(rowsWithoutFilter('hasPhone'), l => l.phone ? 'Has phone' : 'No phone', ['Has phone', 'No phone'],
      v => v === 'Has phone' ? 'Har telefon' : 'Inget telefon'),
    [rowsWithoutFilter])
  const emailValues = useMemo(
    () => buildValues(rowsWithoutFilter('emailStatus'), l => EMAIL_STATUS_LABEL[l.emailStatus] || 'Okänd',
      ['Levererbar', 'Riskabel', 'Studsat', 'Okänd']),
    [rowsWithoutFilter])
  const callCountValues = useMemo(
    () => buildValues(
      rowsWithoutFilter('callCount'),
      l => {
        const c = l.attempts.filter(Boolean).length
        return c >= 4 ? '4+' : String(c)
      },
      ['0', '1', '2', '3', '4+'],
    ),
    [rowsWithoutFilter])
  const yesNoLabel = (v: string) => v === 'Yes' ? 'Ja' : 'Nej'
  const gotContactValues = useMemo(
    () => buildValues(rowsWithoutFilter('gotContact'), l => l.gotContact ? 'Yes' : 'No', ['Yes', 'No'], yesNoLabel),
    [rowsWithoutFilter])
  const ratingValues = useMemo(
    () => buildValues(
      rowsWithoutFilter('rating'),
      l => l.feedback?.rating ? `${l.feedback.rating}★` : 'Unrated',
      ['5★', '4★', '3★', '2★', '1★', 'Unrated'],
      v => v === 'Unrated' ? 'Obetygsatt' : v,
    ),
    [rowsWithoutFilter])
  const stageValues = useMemo(
    () => buildValues(rowsWithoutFilter('stage'), l => l.stage, [...STAGES], stageLabel),
    [rowsWithoutFilter])
  // No `order` argument on purpose: the Salesforce status set is open, so an
  // order list would silently sort a value it had never heard of to the front.
  const sfStatusValues = useMemo(
    () => buildValues(rowsWithoutFilter('sfStatus'), sfStatusLabelOf),
    [rowsWithoutFilter])
  const bookedValues = useMemo(
    () => buildValues(rowsWithoutFilter('booked'), l => l.bookedMeeting ? 'Yes' : 'No', ['Yes', 'No'], yesNoLabel),
    [rowsWithoutFilter])
  const likelyValues = useMemo(
    () => buildValues(rowsWithoutFilter('likely'), l => l.likelyBooking ? 'Yes' : 'No', ['Yes', 'No'], yesNoLabel),
    [rowsWithoutFilter])
  const leadDateValues = useMemo(
    () => buildValues(rowsWithoutFilter('leadDate'), l => leadDateBucketOf(l.leadDate), LEAD_DATE_ORDER),
    [rowsWithoutFilter])
  const moveDateValues = useMemo(
    () => buildValues(rowsWithoutFilter('moveDate'), l => moveDateBucketOf(l.moveDate), MOVE_DATE_ORDER),
    [rowsWithoutFilter])
  const meetingDateValues = useMemo(
    () => buildValues(rowsWithoutFilter('meetingDate'), l => meetingDateBucketOf(l.meetingAt), MEETING_DATE_ORDER),
    [rowsWithoutFilter])
  // Only the rendered label is masked. `value` stays the real string because it
  // is what the filter matches on (see `f.owner.has(ownerLabelOf(l))`) and what
  // gets persisted — masking it would silently stop the filter matching anything.
  const maskLabel = useCallback((raw: string) => {
    if (!anonymised) return raw
    if (raw === '(unassigned)' || raw === UNCLAIMED_LABEL || raw === '(blank)') return raw
    // The owner column keys on the local-part, so resolve it back to the full
    // address first — otherwise the same person reads as one label here and a
    // different one in the timeline.
    const email = raw.includes('@')
      ? raw
      : roster.find(e => e.split('@')[0].toLowerCase() === raw.toLowerCase()) ?? raw
    return person(email)
  }, [anonymised, person, roster])

  const sellerValues = useMemo(
    () => buildValues(rowsWithoutFilter('seller'), l => l.seller || '(unassigned)', undefined, maskLabel),
    [rowsWithoutFilter, maskLabel])
  const ownerValues = useMemo(
    () => buildValues(rowsWithoutFilter('owner'), ownerLabelOf, undefined, maskLabel),
    [rowsWithoutFilter, maskLabel])

  const values: TableValues = {
    region: regionValues,
    city: cityValues,
    score: scoreValues,
    phone: phoneValues,
    email: emailValues,
    callCount: callCountValues,
    gotContact: gotContactValues,
    rating: ratingValues,
    stage: stageValues,
    sfStatus: sfStatusValues,
    booked: bookedValues,
    likely: likelyValues,
    leadDate: leadDateValues,
    moveDate: moveDateValues,
    meetingDate: meetingDateValues,
    seller: sellerValues,
    owner: ownerValues,
  }

  return {
    sorts,
    filters,
    setFilter,
    setFilters,
    sorted,
    sortByColumn,
    sortFor,
    sortRank,
    onSortChange,
    clearSorts,
    clearFilters,
    activeFilterCount,
    values,
  }
}
